import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  FolderIcon,
  ChevronRightIcon,
  PlusIcon,
  CheckIcon,
  TrashIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/apiService";
import { GoogleDriveAttachment, UserRole } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { OverflowInfoCell } from "../../pages/manager-dashboard/OverflowInfoCell";
import { useTranslation } from "react-i18next";

type PathEntry = { id: string | null; name: string };

const normalizeFolderName = (value: string): string =>
  value.toLocaleLowerCase("tr-TR");

const PROTECTED_FINANCE_FOLDER_NAMES = new Set(
  ["Faturalar", "İş Tutarı - Teklifler", "Tahsilatlar"].map((name) =>
    normalizeFolderName(name)
  )
);

const isProtectedFinanceFolder = (folder: GoogleDriveAttachment): boolean =>
  folder.mimeType === "application/vnd.google-apps.folder" &&
  Boolean(folder.name) &&
  PROTECTED_FINANCE_FOLDER_NAMES.has(normalizeFolderName(folder.name));

interface ProjectFolderSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowInstanceId: number | null;
  initialFolderId?: string | null;
  initialPath?: PathEntry[];
  rootLabel?: string;
  onSelect: (folderId: string | null, path: PathEntry[]) => void;
}

const ProjectFolderSelectModal: React.FC<ProjectFolderSelectModalProps> = ({
  isOpen,
  onClose,
  workflowInstanceId,
  initialFolderId,
  initialPath,
  rootLabel,
  onSelect,
}) => {
  const { t } = useTranslation(["projects", "common"]);
  const { user, isAdmin, isManager } = useAuth();
  const rootFolderLabel = t("projects:projectFilesModal.rootFolder");

  const canCreateFolders = useMemo(
    () => user?.role !== UserRole.VISITOR,
    [user?.role]
  );
  const canDeleteFolders = useMemo(() => isAdmin() || isManager(), [isAdmin, isManager]);

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<PathEntry[]>([
    { id: null, name: rootLabel?.trim() || rootFolderLabel },
  ]);
  const [folders, setFolders] = useState<GoogleDriveAttachment[]>([]);
  const [readOnlyFiles, setReadOnlyFiles] = useState<GoogleDriveAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newFolderName, setNewFolderName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<GoogleDriveAttachment | null>(null);
  const currentFolderIdRef = useRef<string | null>(null);
  const openSessionRef = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      setFolderToDelete(null);
      setReadOnlyFiles([]);
      return;
    }
    openSessionRef.current += 1;
    setError(null);
    setNewFolderName("");
    setCreating(false);
    setFolderToDelete(null);
    setFolders([]);
    setReadOnlyFiles([]);

    const nextFolderId = initialFolderId || null;
    currentFolderIdRef.current = nextFolderId;
    setCurrentFolderId(nextFolderId);
    if (initialPath && initialPath.length > 0) {
      setFolderPath(initialPath);
    } else {
      setFolderPath([{ id: null, name: rootLabel?.trim() || rootFolderLabel }]);
    }
  }, [isOpen, initialFolderId, initialPath, rootLabel]);

  useEffect(() => {
    if (!isOpen || !workflowInstanceId) {
      return;
    }

    const load = async () => {
      const requestSession = openSessionRef.current;
      const requestedFolderId = currentFolderId;
      try {
        setLoading(true);
        setError(null);
        const files = await apiService.getWorkflowInstanceFiles(
          workflowInstanceId,
          requestedFolderId
        );
        if (
          openSessionRef.current !== requestSession ||
          currentFolderIdRef.current !== requestedFolderId
        ) {
          return;
        }
        const onlyFolders = files
          .filter((file) => file.mimeType === "application/vnd.google-apps.folder")
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
        const onlyFiles = files
          .filter((file) => file.mimeType !== "application/vnd.google-apps.folder")
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
        setFolders(onlyFolders);
        setReadOnlyFiles(onlyFiles);
      } catch (err) {
        setError(t("projects:projectFilesModal.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, workflowInstanceId, currentFolderId]);

  const handleFolderOpen = (folder: GoogleDriveAttachment) => {
    if (!folder?.id) return;
    currentFolderIdRef.current = folder.id;
    setCurrentFolderId(folder.id);
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index: number) => {
    const target = folderPath[index];
    currentFolderIdRef.current = target.id;
    setCurrentFolderId(target.id);
    setFolderPath(folderPath.slice(0, index + 1));
  };

  const handleCreateFolder = async () => {
    if (!workflowInstanceId) return;
    const name = newFolderName.trim();
    if (!name) return;

    try {
      setCreating(true);
      setError(null);
      const created = await apiService.createProjectFolder(
        workflowInstanceId,
        name,
        currentFolderId
      );
      setNewFolderName("");

      // Navigate into the created folder immediately.
      if (created?.id) {
        currentFolderIdRef.current = created.id;
        setCurrentFolderId(created.id);
        setFolderPath((prev) => [...prev, { id: created.id, name: created.name }]);
      }
    } catch (err) {
      setError(t("projects:folderSelectModal.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  const handleSelectCurrentFolder = () => {
    onSelect(currentFolderId, folderPath);
    onClose();
  };

  const handleDeleteFolder = async (folder: GoogleDriveAttachment) => {
    if (!workflowInstanceId || !folder?.id) return;
    if (!canDeleteFolders) return;

    try {
      setDeletingFolderId(folder.id);
      await apiService.deleteWorkflowInstanceFolder(workflowInstanceId, folder.id);
      setFolders((prev) => prev.filter((item) => item.id !== folder.id));
    } catch (err) {
      setError(t("projects:folderSelectModal.deleteFailed"));
    } finally {
      setDeletingFolderId(null);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[180] p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-white/20 p-2 rounded-lg">
              <FolderIcon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold truncate">
                {t("projects:folderSelectModal.title")}
              </h3>
              <p className="text-xs text-blue-100 truncate">
                {t("projects:folderSelectModal.subtitle")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          {!workflowInstanceId ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              {t("projects:folderSelectModal.selectProjectFirst")}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-1 text-sm text-gray-700">
                  {folderPath.map((crumb, index) => (
                    <button
                      key={`${crumb.id || "root"}-${index}`}
                      type="button"
                      onClick={() => handleBreadcrumbClick(index)}
                      className={`flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors ${
                        index === folderPath.length - 1
                          ? "text-gray-900 font-semibold"
                          : "text-blue-600 hover:text-blue-700"
                      }`}
                    >
                      <span>{crumb.name}</span>
                      {index < folderPath.length - 1 && (
                        <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>

                {canCreateFolders && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder={t("projects:folderSelectModal.newFolderName")}
                      className="w-44 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      disabled={creating}
                    />
                    <button
                      type="button"
                      onClick={handleCreateFolder}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={creating || !newFolderName.trim()}
                      title={t("projects:folderSelectModal.createFolder")}
                    >
                      <PlusIcon className="h-4 w-4" />
                      {t("projects:folderSelectModal.create")}
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <div>
                    {folders.length === 0 && readOnlyFiles.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-600 bg-gray-50">
                        {t("projects:folderSelectModal.emptyFolder")}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {folders.map((folder) => (
                          <div
                            key={folder.id}
                            className="flex items-center gap-2 px-2 py-1 min-w-0"
                          >
                            <button
                              type="button"
                              onClick={() => handleFolderOpen(folder)}
                              className="flex-1 min-w-0 px-2 py-2 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 rounded-lg"
                            >
                              <FolderIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                              <OverflowInfoCell
                                value={folder.name}
                                fullValue={folder.name}
                                title={t("projects:projectFilesModal.folderName")}
                                overflowOnly
                                portalTooltip
                                cellClassName="min-w-0 flex-1"
                                textClassName="text-sm font-medium text-gray-900"
                                tooltipClassName="w-80 max-w-[32rem]"
                              />
                              <ChevronRightIcon className="ml-auto h-5 w-5 text-gray-400" />
                            </button>
                            {canDeleteFolders &&
                              !isProtectedFinanceFolder(folder) && (
                              <button
                                type="button"
                                onClick={() => setFolderToDelete(folder)}
                                className="inline-flex items-center justify-center rounded-lg p-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                title={t("projects:projectFilesModal.deleteFolder")}
                                aria-label={t("projects:projectFilesModal.deleteFolder")}
                                disabled={deletingFolderId === folder.id}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                              )}
                            {canDeleteFolders &&
                              isProtectedFinanceFolder(folder) && (
                              <div className="h-8 w-8 flex-shrink-0" aria-hidden="true" />
                              )}
                          </div>
                        ))}
                        {readOnlyFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center gap-2 px-2 py-1 min-w-0"
                          >
                            <div className="flex-1 min-w-0 px-2 py-2 flex items-center gap-3 rounded-lg">
                              {file.iconUrl ? (
                                <img
                                  src={file.iconUrl}
                                  alt=""
                                  className="h-5 w-5 flex-shrink-0"
                                />
                              ) : (
                                <DocumentTextIcon className="h-5 w-5 text-slate-400 flex-shrink-0" />
                              )}
                              <OverflowInfoCell
                                value={file.name}
                                fullValue={file.name}
                                title={t("projects:projectFilesModal.fileName")}
                                overflowOnly
                                portalTooltip
                                cellClassName="min-w-0 flex-1"
                                textClassName="text-sm font-medium text-gray-800"
                                tooltipClassName="w-80 max-w-[32rem]"
                              />
                            </div>
                            {canDeleteFolders && <div className="w-8 flex-shrink-0" />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
          >
            {t("common:cancel")}
          </button>
          <button
            type="button"
            onClick={handleSelectCurrentFolder}
            disabled={!workflowInstanceId}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transition-colors font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            title={t("projects:folderSelectModal.selectCurrentFolder")}
          >
            <CheckIcon className="h-5 w-5" />
            {t("projects:folderSelectModal.selectThisFolder")}
          </button>
        </div>
      </div>
      <ConfirmDialog
        isOpen={Boolean(folderToDelete)}
        onClose={() => setFolderToDelete(null)}
        onConfirm={() => {
          if (folderToDelete) {
            void handleDeleteFolder(folderToDelete);
          }
        }}
        title={t("projects:projectFilesModal.deleteFolderTitle")}
        message={
          folderToDelete
            ? t("projects:projectFilesModal.deleteFolderMessage", {
                name: folderToDelete.name,
              })
            : ""
        }
        confirmText={t("projects:projectFilesModal.confirmDelete")}
        cancelText={t("common:cancel")}
        type="danger"
      />
    </div>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default ProjectFolderSelectModal;
